import Vue from 'vue';
import axios from 'axios';

// --------------------------------
// Embut dels dossiers i experiments A/B dels anuncis
// Cada visitant té un id anònim i una variant fixa per experiment.
// Els visitants nous reben les variants segons el repartiment del servidor, que cada 3 dies
// dona més trànsit a les que venen més (server/routes/shop/experiments.js).
// La llista del servidor mana; aquesta només és la de reserva. La primera variant és el control.
// --------------------------------
export const EXPERIMENTS = {
  popup_timing: ['5min', '2min', 'off'],
  cta_text: ['veure_mes', 'dossier_assignatura'],
  solution_ad_creative: ['cart', 'community'],
};

const AD_TOUCH_TTL = 1000 * 60 * 60 * 24 * 7;
const memory = {};

function read(key) {
  try {
    const value = localStorage.getItem(key);
    if (value !== null) return value;
  } catch (e) {
    // localStorage bloquejat
  }
  return key in memory ? memory[key] : null;
}

function write(key, value) {
  memory[key] = value;
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // localStorage bloquejat
  }
}

function parse(value, fallback) {
  try {
    const parsed = JSON.parse(value);
    return parsed === null ? fallback : parsed;
  } catch (e) {
    return fallback;
  }
}

function uuid() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

const today = () => new Date().toISOString().slice(0, 10);

const visitorId = read('funnel_visitor_id') || uuid();
write('funnel_visitor_id', visitorId);

if (read('funnel_referrer') === null) {
  let referrer = '';
  try {
    const host = new URL(document.referrer).hostname;
    if (host !== window.location.hostname) referrer = host;
  } catch (e) {
    // sense referrer
  }
  write('funnel_referrer', referrer);
}


// --------------------------------
// Assignació de variants
// --------------------------------
const state = Vue.observable({variants: {}});
const stored = parse(read('funnel_variants'), {});

function pick(options, weights = {}) {
  const values = options.map(option => Math.max(0, Number(weights[option]) || 0));
  const total = values.reduce((sum, value) => sum + value, 0);
  if (!total) return options[Math.floor(Math.random() * options.length)];

  let r = Math.random() * total;
  for (let i = 0; i < options.length; i++) {
    r -= values[i];
    if (r < 0) return options[i];
  }
  return options[options.length - 1];
}

let resolveReady;
const ready = new Promise(resolve => {
  resolveReady = resolve;
});

let assigned = false;

function assign(experiments, weights) {
  if (assigned) return;
  assigned = true;

  const variants = {};
  Object.entries(experiments).forEach(([name, options]) => {
    variants[name] = options.includes(stored[name]) ? stored[name] : pick(options, weights[name]);
  });

  state.variants = variants;
  write('funnel_variants', JSON.stringify(variants));
  resolveReady();
}

const needsWeights = Object.entries(EXPERIMENTS).some(([name, options]) => !options.includes(stored[name]));

if (!needsWeights) {
  assign(EXPERIMENTS, {});
} else {
  // Després que main.ts configuri la baseURL d'axios
  setTimeout(() => {
    axios.get('/track/weights', {timeout: 3000})
      .then(({data}) => assign(data.experiments || EXPERIMENTS, data.weights || {}))
      .catch(() => assign(EXPERIMENTS, {}));
  }, 0);
  setTimeout(() => assign(EXPERIMENTS, {}), 4000);
}


// --------------------------------
// Enviament d'esdeveniments
// --------------------------------
function lastAdTouch() {
  const touch = parse(read('funnel_ad_touch'), null);
  return touch && Date.now() - touch.at < AD_TOUCH_TTL ? touch.placement : null;
}

function send(event) {
  ready.then(() => {
    const payload = {visitor_id: visitorId, events: [event]};

    // Les dades del visitant només s'envien un cop al dia
    if (read('funnel_synced') !== today()) {
      payload.visitor = {
        variants: state.variants,
        traffic_source: {referrer: read('funnel_referrer') || null, ...parse(read('meta_traffic_source'), {})},
        device: window.innerWidth < 960 ? 'mobile' : 'desktop',
      };
      write('funnel_synced', today());
    }

    axios.post('/track', payload).catch(() => {});
  });
}

const funnel = {
  visitorId,
  ready,
  variant(name) {
    return state.variants[name];
  },
  track(event, {placement = null, subject_id = null, book_id = null, value = null, meta = null} = {}) {
    const adSource = lastAdTouch();
    send({
      event,
      placement,
      subject_id,
      book_id,
      value,
      path: window.location.pathname,
      meta: adSource ? {...meta, ad_source: adSource} : meta,
    });
  },
  // Impressions: n'hi ha prou amb saber que el visitant l'ha vist, no quantes vegades
  trackOncePerDay(event, props = {}) {
    const key = `funnel_once_${event}_${props.placement || ''}`;
    if (read(key) === today()) return;
    write(key, today());
    funnel.track(event, props);
  },
  adClick(placement, props = {}) {
    write('funnel_ad_touch', JSON.stringify({placement, at: Date.now()}));
    funnel.track('ad_click', {...props, placement});
  },
};

Vue.prototype.$funnel = funnel;

export default funnel;
